import { bind, classNames, Component, createRef, h, signal, tag } from "omi";
import { Editor, type JSONContent } from "@tiptap/core";
import { EditorView } from "@tiptap/pm/view";
import debounce from "lodash-es/debounce";
import isEmpty from "lodash-es/isEmpty";
import tippyAnimations from "tippy.js/animations/scale-subtle.css?inline";

import { NoteLoadingSkeleton } from "@/components/nw-note/skeleton";
import editorStyles from "@/editor/styles.css?inline";
import { useEditorController } from "@/editor/useEditor";
import { COMMUNITY_LINK_WITH_ERROR_UTM } from "@/globals/links";
import globalStyles from "@/globals/styles.css?inline";
import { Timeout } from "@/types/timers";
import { tailwind } from "@/tailwind";

import { NWButton } from "../atoms/nw-button";
import { NWFeedback } from "../nw-feedback";
import { NW_NOTE_CORE_EXTENSIONS } from "./adapter";
import { BaseNoteProps, NOTE_CONTENT_UPDATE_EVENT, NoteType, type AskNoteProps, type NWNoteProps } from "./models";

@tag("nw-note")
export class NWNote extends Component<NWNoteProps> {
  static css = [
    tailwind,
    globalStyles,
    tippyAnimations,
    editorStyles,
    `
    :host { padding: 8px 0; display: block; }
    .ProseMirror { min-height: 80px; outline: none; white-space: pre-wrap; }
    .ProseMirror p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: #adb5bd;
      pointer-events: none;
      height: 0;
    }
    `,
  ];

  static propTypes = {
    id: String,
    heading: String,
    notetype: String,
    group: String,
    highlightContext: String,
    surroundingContext: String,
    loading: Boolean,
    error: String,
    initialContent: Object,
    placeholder: String,
    // Ask Note specific props
    query: String,
  };

  private readonly RESIZE_DEBOUNCE_MS = 100;
  private readonly CHARACTER_LIMIT = 300;
  private readonly MIN_HEIGHT = 200;

  private noteRef: HTMLDivElement | null = null;
  private headingInputRef = createRef<HTMLTextAreaElement>();
  private editorContentRef = createRef<HTMLDivElement>();

  private initialHeight = 0;
  private initialY = 0;
  private preExpandHeight = 0;
  private maxContentHeightForResize = 0;
  private hasManuallyResized = signal(false);
  private isExpanded = signal(false);
  private noteHeight = signal<string | null>(null);
  private editor: Editor | null = null;
  private resizeTimeout: Timeout | undefined;
  private debouncedHandleResize = debounce(this.handleResize, this.RESIZE_DEBOUNCE_MS);

  /** Temporary property for initial content */
  public _initialContentForEditor?: JSONContent | string;
  public _editorInstanceId?: string;

  installed(): void {
    this.initializeEditorIfNeeded();
    this.setupResizeListeners();
  }

  receiveProps(newProps: NWNoteProps, oldProps: NWNoteProps): void {
    this.updatePlaceholder(newProps, oldProps);
  }

  uninstall(): void {
    this.cleanupEditor();
    this.cleanupResizeListeners();
  }

  private initializeEditorIfNeeded(): void {
    if (this.editorContentRef.current && !this.editor) {
      const content = this._initialContentForEditor ?? this.props.initialContent;
      this.initializeEditor(content);
      this._initialContentForEditor = undefined;
    }
  }

  private setupResizeListeners(): void {
    if (this.headingInputRef.current) {
      window.addEventListener("resize", this.debouncedHandleResize, { passive: true });
      this.resizeTimeout = setTimeout(() => {
        if (this.headingInputRef.current) {
          this.resizeElement(this.headingInputRef.current);
        }
      }, 0);
    }
  }

  private updatePlaceholder(newProps: NWNoteProps, oldProps: NWNoteProps): void {
    if (newProps.placeholder !== oldProps.placeholder && this.editor && !this.editor.isDestroyed) {
      const placeholderExtension = this.editor.extensionManager.extensions.find(ext => ext.name === "placeholder");

      if (placeholderExtension) {
        placeholderExtension.options.placeholder = newProps.placeholder ?? "Start typing your note...";
        this.editor.view.dispatch(this.editor.state.tr);
      }
    }
  }

  private cleanupEditor(): void {
    this.editor?.destroy();
    this.editor = null;
  }

  private cleanupResizeListeners(): void {
    if (this.headingInputRef) {
      window.removeEventListener("resize", this.debouncedHandleResize);
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.debouncedHandleResize.cancel();
  }

  private parseInitialContent(initialContentOverride?: JSONContent | string): JSONContent {
    const contentToUse = initialContentOverride ?? this.props.initialContent;
    let validContent: JSONContent;

    if (contentToUse && typeof contentToUse === "object") {
      validContent = contentToUse;
    } else if (typeof contentToUse === "string") {
      try {
        validContent = contentToUse.trim().startsWith("{")
          ? JSON.parse(contentToUse)
          : { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: contentToUse }] }] };
      } catch (error) {
        console.warn(`[NWNote ${this.props.id}] Failed to parse content:`, error);
        validContent = { type: "doc", content: [{ type: "paragraph" }] };
      }
    } else {
      validContent = { type: "doc", content: [{ type: "paragraph" }] };
    }

    return validContent;
  }

  private initializeEditor(initialContentOverride?: JSONContent | string): void {
    if (this.editor || !this.editorContentRef.current) {
      return;
    }

    const { placeholder } = this.props as BaseNoteProps;
    const validInitialContent = this.parseInitialContent(initialContentOverride);

    this.editor = new Editor({
      element: this.editorContentRef.current,
      editable: true,
      autofocus: false,
      injectCSS: true,
      extensions: NW_NOTE_CORE_EXTENSIONS(placeholder),
      content: validInitialContent,
      onUpdate: ({ editor }) => {
        const contentJson = editor.getJSON();
        const event = new CustomEvent<JSONContent>(NOTE_CONTENT_UPDATE_EVENT, {
          detail: contentJson,
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(event);
      },
      editorProps: {
        handleClick: (_view: EditorView, _pos: number, event: MouseEvent) => {
          const target = event.target as Node | null;
          const targetElement = target instanceof Element ? target : null;

          const closestTabs = targetElement?.closest("nw-tabs");
          const closestTabPanel = targetElement?.closest("nw-tab");

          if (closestTabs && !closestTabPanel && this.editorContentRef.current?.contains(closestTabs)) {
            return true;
          }

          if (this.editorContentRef.current && !this.editorContentRef.current.contains(target)) {
            return true;
          }

          return false;
        },
      },
    });
  }

  public updateContent(newContent: JSONContent | string): void {
    if (!this.editor || this.editor.isDestroyed) {
      console.warn(`[NWNote ${this.props.id}] updateContent called but editor is not available.`);
      return;
    }

    const parsedContent = this.parseInitialContent(newContent);
    const currentContent = this.editor.getJSON();

    if (JSON.stringify(currentContent) !== JSON.stringify(parsedContent)) {
      this.editor.commands.setContent(parsedContent, false);
    }
  }

  @bind
  private handleResize(): void {
    if (this.headingInputRef.current) {
      this.resizeElement(this.headingInputRef.current);
    }
  }

  @bind
  private handleClick(event: MouseEvent): void {
    const target = event.target as Node | null;

    if (this.editorContentRef.current?.contains(target)) {
      return;
    }

    if (target instanceof Node && hasInPath(event, "nw-highlight")) {
      return;
    }
  }

  @bind
  private retry() {
    const action = this.props.notetype;

    this.removeAttribute("error");
    this.setAttribute("loading", "true");
    this.update();

    const controller = useEditorController();
    if (!controller) return;

    switch (action) {
      case NoteType.SIMPLIFY:
        controller.simplify(this.props.id, this.props.group);
        break;
      case NoteType.EXPLAIN:
        controller.explain(this.props.id, this.props.group);
        break;
      case NoteType.EXAMPLES:
        controller.examples(this.props.id, this.props.group);
        break;
      case NoteType.ASK:
        controller.ask(this.props.id, this.props.group, (this.props as AskNoteProps).query);
        break;
      default:
        this.removeAttribute("loading");
        this.update();
        break;
    }
  }

  @bind
  private handleDeleteNote(event: MouseEvent) {
    event.stopPropagation();
    const controller = useEditorController(this._editorInstanceId);
    if (!controller) return;
    controller.deleteNote(this.id);
    controller.setActiveHighlightGroup(this.props.group, false);
  }

  @bind
  private getDefaultHeading(type: NoteType): string {
    switch (type) {
      case NoteType.BLANK:
        return "Name your note...";
      case NoteType.SIMPLIFY:
        return "Simplify...";
      case NoteType.EXPLAIN:
        return "Explanation";
      case NoteType.EXAMPLES:
        return "Examples";
      case NoteType.ASK:
        return (this.props as AskNoteProps).query || "Ask...";
      default:
        return "...";
    }
  }

  @bind
  private handleHeadingChange(e: Event) {
    const target = e.target as HTMLTextAreaElement;
    const value = target.value;

    if (value.length > this.CHARACTER_LIMIT) {
      target.value = value.substring(0, this.CHARACTER_LIMIT);
      return;
    }

    this.resizeElement(target);
    useEditorController()?.updateNoteHeading(this.props.id, value);
  }

  @bind
  private resizeElement(e: HTMLTextAreaElement) {
    // Reset height to auto first to get the correct scrollHeight
    e.style.height = "auto";
    e.style.height = e.scrollHeight + "px";
  }

  @bind
  private handleHeadingKeydown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      (event.target as HTMLElement).blur();
    }
  }

  @bind
  private handleResizeStart(e: PointerEvent) {
    e.preventDefault();

    if (!this.noteRef) return;

    const height = this.noteRef.offsetHeight;
    this.initialHeight = height;
    this.noteHeight.value = `${height}px`;
    this.initialY = e.clientY;
    this.maxContentHeightForResize = this.calculateMaxContentHeight();

    const target = e.target as HTMLElement;
    target.setPointerCapture(e.pointerId);

    target.addEventListener("pointermove", this.handleResizeMove);
    target.addEventListener("pointerup", this.handleResizeEnd);
  }

  @bind
  private handleResizeMove(e: PointerEvent) {
    if (!this.noteRef) return;

    const deltaY = e.clientY - this.initialY;
    const newHeight = Math.min(Math.max(this.initialHeight + deltaY, this.MIN_HEIGHT), this.maxContentHeightForResize);

    this.noteHeight.value = `${newHeight}px`;
    this.hasManuallyResized.value = true;

    if (this.isExpanded.value) {
      this.isExpanded.value = false;
    }

    this.preExpandHeight = newHeight;
  }

  @bind
  private handleResizeDoubleClick(e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();

    if (!this.noteRef) return;

    if (this.isExpanded.value) {
      this.noteHeight.value = `${this.preExpandHeight}px`;
      this.isExpanded.value = false;
    } else {
      this.preExpandHeight = this.noteRef.offsetHeight;
      const totalHeight = this.calculateMaxContentHeight();

      const heightDifference = totalHeight - this.preExpandHeight;
      if (heightDifference > 0 && heightDifference < 50) {
        this.preExpandHeight = Math.max(this.MIN_HEIGHT, Math.floor(this.preExpandHeight * 0.75));
      }

      this.noteHeight.value = `${Math.max(totalHeight, this.MIN_HEIGHT)}px`;
      this.isExpanded.value = true;
    }

    this.hasManuallyResized.value = true;
  }

  @bind
  private handleResizeEnd(e: PointerEvent) {
    const target = e.target as HTMLElement;
    target.releasePointerCapture(e.pointerId);

    target.removeEventListener("pointermove", this.handleResizeMove);
    target.removeEventListener("pointerup", this.handleResizeEnd);
  }

  @bind
  private calculateMaxContentHeight(): number {
    const contentHeight = this.editorContentRef.current?.scrollHeight || 0;
    const headingHeight = this.headingInputRef.current?.offsetHeight || 0;
    const paddingHeight = 140;
    return contentHeight + headingHeight + paddingHeight;
  }

  private isLongFormNote(): boolean {
    return [NoteType.EXPLAIN, NoteType.EXAMPLES, NoteType.ASK].includes(this.props.notetype);
  }

  render({ notetype, heading, error, loading = false, ...props }: NWNoteProps) {
    const isAINote = [NoteType.SIMPLIFY, NoteType.EXPLAIN, NoteType.EXAMPLES, NoteType.ASK].includes(notetype);
    const askProps = props as AskNoteProps;
    const isLongFormNote = this.isLongFormNote();

    return (
      <div>
        <div
          ref={el => (this.noteRef = el)}
          onClick={this.handleClick}
          class={classNames(
            "bg-white rounded-3xl border w-full pt-6 px-6 pb-2 space-y-1 relative flex flex-col transition-all duration-300 ease-in-out",
            { "min-h-[16rem]": !this.hasManuallyResized.value },
            { "animate-fadeInFast": loading },
            { "h-fit max-h-80": !this.hasManuallyResized.value && !isLongFormNote },
            { "max-h-[30rem]": !this.hasManuallyResized.value && isLongFormNote },
            { "h-auto": this.hasManuallyResized.value && !this.noteHeight.value },
            { "my-8": !isAINote, "mt-8 mb-2": isAINote },
          )}
          style={{ height: this.noteHeight.value || undefined }}
        >
          {/* Heading Section */}
          <div class="flex items-start justify-between bg-graysecondary-50 pb-3 mb-4 p-4 rounded-lg self-stretch flex-shrink-0">
            <textarea
              maxLength={this.CHARACTER_LIMIT}
              ref={this.headingInputRef}
              class={classNames("text-primary bg-transparent text-lg outline-none overflow-hidden w-full resize-none", {
                "placeholder:text-primary": notetype !== NoteType.BLANK,
              })}
              onInput={this.handleHeadingChange}
              onKeyDown={this.handleHeadingKeydown}
              value={isEmpty(heading) && notetype === NoteType.ASK ? askProps.query : heading}
              placeholder={this.getDefaultHeading(notetype)}
              rows={1}
            />
            <div class="flex items-center space-x-2 pl-2">
              <NWButton
                size="fit"
                type="icon"
                tabIndex={-1}
                icon="trash"
                iconSize="md"
                onClick={this.handleDeleteNote}
                backgroundTransparent
              />
            </div>
          </div>

          {/* Content Area */}
          <div class="overflow-y-auto flex-grow flex flex-col">
            {!isEmpty(error) ? (
              <div className="flex flex-col h-full items-center justify-center p-4 mb-4 border-error-300 rounded-lg">
                <p className="text-sm text-center w-2/3 text-error-600">
                  Oops! Looks like our bot took an unexpected coffee break. Let's give it another shot!{" "}
                  <a
                    href={COMMUNITY_LINK_WITH_ERROR_UTM}
                    target="_blank"
                    class="underline underline-offset-2 transition-colors hover:text-error-600"
                  >
                    Let us know
                  </a>{" "}
                  if the problem persists.
                </p>
                <NWButton type="secondary-color" size="fit" onClick={this.retry}>
                  Try again
                </NWButton>
              </div>
            ) : loading ? (
              <div className="my-4">
                <NoteLoadingSkeleton />
              </div>
            ) : (
              <>
                <div class="flex flex-col flex-1 break-words whitespace-normal">
                  <div ref={this.editorContentRef} class="flex-1 prose prose-sm max-w-none focus:outline-none h-full" />
                </div>
                <div class="sticky bottom-0 left-0 right-4 bg-white">
                  <div class="absolute -top-8 left-0 right-4 h-8 pointer-events-none bg-gradient-to-t from-white to-transparent"></div>
                  {/* Feedback Section */}
                  {isAINote && !loading && isEmpty(error) && (
                    <div class="py-1">
                      <NWFeedback feature="Note" analyticsOptions={{ group: this.props.group, type: notetype }} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Resize Handle */}
          <div
            class="absolute bottom-2 right-2 w-5 h-5 flex justify-end items-end p-0.5 cursor-nwse-resize z-10"
            onPointerDown={this.handleResizeStart}
            onDblClick={this.handleResizeDoubleClick}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="19" viewBox="0 0 21 19" fill="none">
              <line x1="0.646447" y1="18.2675" x2="17.6464" y2="1.26754" stroke="#D0D5DD" />
              <line x1="8.64645" y1="17.7675" x2="17.1459" y2="9.26812" stroke="#D0D5DD" />
              <line x1="15.6464" y1="18.5175" x2="19.8962" y2="14.2678" stroke="#D0D5DD" />
            </svg>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Checks if an event's composed path contains an element with the specified tag name
 * @param event - The event to check
 * @param tagName - The tag name to look for
 * @returns True if the tag is found in the event path
 */
function hasInPath(event: Event, tagName: string): boolean {
  const path = event.composedPath();
  return path.some(
    (element): element is HTMLElement => element instanceof HTMLElement && element.tagName.toLowerCase() === tagName,
  );
}
